import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const PRODUCTS_WITH_MESSAGE = new Set([1, 2, 3, 4, 56, 57, 58]);

function isVariablePrice(item) {
  return item.tip_pret === "variabil" || item.tip_pret === "kg";
}

function isCustomCake(item) {
  return item.este_tort_personalizat === true;
}

function requiresMessage(productOrItem) {
  return PRODUCTS_WITH_MESSAGE.has(Number(productOrItem.id));
}

function roundPrice(value) {
  return Math.round(Number(value || 0) * 2) / 2;
}

function getDiscountedUnitPrice(item) {
  const pret = Number(item.pret || 0);
  const reducereTip = item.reducere_tip || "procent";
  const reducere = Number(item.reducere_procent || 0);
  const pretRedus = Number(item.pret_redus || 0);

  if (reducereTip === "pret_nou" && pretRedus > 0 && pretRedus < pret) {
    return pretRedus;
  }

  if (reducereTip === "procent" && reducere > 0) {
    return roundPrice(pret - (pret * reducere) / 100);
  }

  return pret;
}

function calcPretFinal(item) {
  const cantitate = Number(item.cantitate || 1);

  if (isVariablePrice(item)) {
    const pretPeKg = isCustomCake(item)
      ? Number(item.personalizare_pret_kg || item.pret || 0)
      : getDiscountedUnitPrice(item);

    const pretDinGramaj = (pretPeKg * Number(item.gramaj || 0)) / 1000;

    return roundPrice(pretDinGramaj * cantitate);
  }

  return roundPrice(getDiscountedUnitPrice(item) * cantitate);
}

function normalizeMessage(msg) {
  return String(msg || "").trim();
}

function buildCartId(product, gramajSelectat, mesajSelectat = "") {
  const normalizedMessage = normalizeMessage(mesajSelectat);

  if (requiresMessage(product) && normalizedMessage) {
    return `${product.id}-${gramajSelectat || "fix"}-msg-${normalizedMessage}`;
  }

  return `${product.id}-${gramajSelectat || "fix"}`;
}

function buildUniqueCartId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("cartItems");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [toast, setToast] = useState("");

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(items));
  }, [items]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 1800);
  };

  const addToCart = (product, options = {}) => {
    const productIsVariable = isVariablePrice(product);
    const productIsCustomCake = product.este_tort_personalizat === true;
    const productNeedsMessage = requiresMessage(product);

    const gramajSelectat = productIsVariable
      ? (options.gramaj ?? "")
      : (product.gramaj ?? null);

    const mesajSelectat = normalizeMessage(options.mesaj_selectat);

    const shouldStaySeparate =
      productIsCustomCake || (productNeedsMessage && !mesajSelectat);

    const cartId = shouldStaySeparate
      ? buildUniqueCartId()
      : buildCartId(product, gramajSelectat, mesajSelectat);

    setItems((prev) => {
      if (!shouldStaySeparate) {
        const existing = prev.find((x) => x.cartId === cartId);

        if (existing) {
          return prev.map((x) =>
            x.cartId === cartId
              ? { ...x, cantitate: x.cantitate + (options.cantitate || 1) }
              : x,
          );
        }
      }

      return [
        ...prev,
        {
          cartId,
          id: product.id,
          nume: product.nume,
          imagine: product.imagine,
          pret: Number(product.pret || 0),
          reducere_procent: Number(product.reducere_procent || 0),
          reducere_tip: product.reducere_tip || "procent",
          pret_redus:
            product.pret_redus != null ? Number(product.pret_redus) : null,
          tip_pret: product.tip_pret || "fix",
          gramaj: gramajSelectat,
          gramaj_selectabil:
            product.gramaj_selectabil ?? productIsVariable ?? false,
          gramaj_min: product.gramaj_min ?? null,
          gramaj_max: product.gramaj_max ?? null,
          pas_gramaj: product.pas_gramaj ?? null,
          termen_ore: product.termen_ore ?? 0,
          cantitate: options.cantitate || 1,

          permite_mentiuni: product.permite_mentiuni ?? true,
          este_tort_personalizat: product.este_tort_personalizat ?? false,
          afiseaza_pret_variabil: product.afiseaza_pret_variabil ?? false,
          afiseaza_per_buc: product.afiseaza_per_buc ?? false,

          mentiuni: options.mentiuni ?? "",
          mesaj_selectat: mesajSelectat,
          personalizare_nume: options.personalizare_nume ?? "",
          personalizare_pret_kg: Number(options.personalizare_pret_kg || 0),
          descriere_decor: options.descriere_decor ?? "",
          poza_model_url: options.poza_model_url ?? "",
        },
      ];
    });

    showToast("✔ Produs adăugat în coș!");
  };

  const removeFromCart = (cartId) => {
    setItems((prev) => prev.filter((x) => x.cartId !== cartId));
  };

  const updateQuantity = (cartId, cantitate) => {
    if (cantitate <= 0) {
      removeFromCart(cartId);
      return;
    }

    setItems((prev) =>
      prev.map((x) => (x.cartId === cartId ? { ...x, cantitate } : x)),
    );
  };

  const updateGramaj = (cartId, gramajNou) => {
    setItems((prev) => {
      const current = prev.find((x) => x.cartId === cartId);
      if (!current) return prev;

      if (isCustomCake(current)) {
        return prev.map((x) =>
          x.cartId === cartId ? { ...x, gramaj: gramajNou } : x,
        );
      }

      const currentMessage = normalizeMessage(current.mesaj_selectat);
      const needsMessage = requiresMessage(current);

      if (needsMessage && !currentMessage) {
        return prev.map((x) =>
          x.cartId === cartId ? { ...x, gramaj: gramajNou } : x,
        );
      }

      const newCartId = buildCartId(current, gramajNou, currentMessage);

      const existingSameVariant = prev.find(
        (x) => x.cartId === newCartId && x.cartId !== cartId,
      );

      if (existingSameVariant) {
        return prev
          .filter((x) => x.cartId !== cartId)
          .map((x) =>
            x.cartId === newCartId
              ? { ...x, cantitate: x.cantitate + current.cantitate }
              : x,
          );
      }

      return prev.map((x) =>
        x.cartId === cartId
          ? { ...x, gramaj: gramajNou, cartId: newCartId }
          : x,
      );
    });
  };

  const updateMentiuni = (cartId, mentiuni) => {
    setItems((prev) =>
      prev.map((x) => (x.cartId === cartId ? { ...x, mentiuni } : x)),
    );
  };

  const updateMesajSelectat = (cartId, mesaj_selectat) => {
    const mesajNou = normalizeMessage(mesaj_selectat);

    setItems((prev) => {
      const current = prev.find((x) => x.cartId === cartId);
      if (!current) return prev;

      if (!requiresMessage(current) || isCustomCake(current)) {
        return prev.map((x) =>
          x.cartId === cartId ? { ...x, mesaj_selectat: mesajNou } : x,
        );
      }

      if (!mesajNou) {
        return prev.map((x) =>
          x.cartId === cartId ? { ...x, mesaj_selectat: "" } : x,
        );
      }

      const newCartId = buildCartId(current, current.gramaj, mesajNou);

      const existingSameVariant = prev.find(
        (x) => x.cartId === newCartId && x.cartId !== cartId,
      );

      if (existingSameVariant) {
        return prev
          .filter((x) => x.cartId !== cartId)
          .map((x) =>
            x.cartId === newCartId
              ? {
                  ...x,
                  cantitate: x.cantitate + current.cantitate,
                  mesaj_selectat: mesajNou,
                }
              : x,
          );
      }

      return prev.map((x) =>
        x.cartId === cartId
          ? {
              ...x,
              mesaj_selectat: mesajNou,
              cartId: newCartId,
            }
          : x,
      );
    });
  };

  const updatePersonalizare = (
    cartId,
    personalizare_nume,
    personalizare_pret_kg,
  ) => {
    setItems((prev) =>
      prev.map((x) =>
        x.cartId === cartId
          ? {
              ...x,
              personalizare_nume,
              personalizare_pret_kg: Number(personalizare_pret_kg || 0),
            }
          : x,
      ),
    );
  };

  const updateDescriereDecor = (cartId, descriere_decor) => {
    setItems((prev) =>
      prev.map((x) => (x.cartId === cartId ? { ...x, descriere_decor } : x)),
    );
  };

  const updatePozaModel = (cartId, poza_model_url) => {
    setItems((prev) =>
      prev.map((x) => (x.cartId === cartId ? { ...x, poza_model_url } : x)),
    );
  };

  const clearCart = () => setItems([]);

  const totalProduse = useMemo(
    () => items.reduce((acc, item) => acc + Number(item.cantitate || 0), 0),
    [items],
  );

  const totalPret = useMemo(
    () => items.reduce((acc, item) => acc + calcPretFinal(item), 0),
    [items],
  );

  return (
    <CartContext.Provider
      value={{
        items,
        toast,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateGramaj,
        updateMentiuni,
        updateMesajSelectat,
        updatePersonalizare,
        updateDescriereDecor,
        updatePozaModel,
        clearCart,
        totalProduse,
        totalPret,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart trebuie folosit în CartProvider");
  return ctx;
}
